import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';
import ErrorToast from '../components/ErrorToast';
import { Check, X } from 'lucide-react';
import { SubscriptionType } from '../models/enums/SubscriptionType';
import { plans } from '../models/SubscriptionPlans';
import { useAuth } from '../context/AuthContext';

const ChangeSubscriptionPage: React.FC = () => {
    const navigate = useNavigate();
    const {user} = useAuth();

    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const email = user?.email ?? '';

    useEffect(() => {
        if (!user || !email) {
            setErrorMessage("We've encountered an error. Please try logging in again.");
        }
    }, [user, email]);

    const freePlan = useMemo(() =>
        plans.find((p) => p.key === SubscriptionType.FREE
        ), []);
    const premiumPlan = useMemo(() =>
        plans.find((p) => p.key === SubscriptionType.PREMIUM
        ), []);

    const canSubmit = !!email && !submitting;

    const handleContinue = (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) {
            setErrorMessage("We've encountered an error. Please try logging in again.");
            return;
        }

        setSubmitting(true);
        navigate('/checkout', {
            state: {
                subscriptionType: SubscriptionType.PREMIUM,
                email,
            }
        });
        setSubmitting(false);
    };

    return (
        <div className="min-h-screen flex flex-col bg-white">
            <NavBar/>
            <main className="flex-1">
                <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                    <header className="text-center mb-8">
                        <h1 className="text-3xl font-bold text-crumbblue">Upgrade to Premium</h1>
                        <p className="text-gray-600 mt-2">
                            Unlock unlimited clients, interactions, follow-ups, AI features, and file uploads.
                        </p>
                    </header>

                    {/* Plan summaries (read-only, from SubscriptionPlans) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                        {freePlan && (
                            <div
                                data-testid="free-summary-card"
                                className="rounded-2xl border border-gray-200 p-5 shadow-sm"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div>
                                        <div className="text-xl font-semibold">{freePlan.name}</div>
                                        <div className="text-sm text-gray-600 mt-1">
                                            {freePlan.highlights.join(' • ')}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <span
                                            className="inline-block rounded-full px-3 py-1 text-sm font-medium bg-gray-100">
                                            {freePlan.priceLabel}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                        {premiumPlan && (
                            <div
                                data-testid="premium-summary-card"
                                className="rounded-2xl border border-crumborange ring-2 ring-crumborange/40 p-5 shadow-sm"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div>
                                        <div className="text-xl font-semibold">{premiumPlan.name}</div>
                                        <div className="text-sm text-gray-600 mt-1">
                                            {premiumPlan.highlights.join(' • ')}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <span
                                            className="inline-block rounded-full px-3 py-1 text-sm font-medium bg-gray-100">
                                            {premiumPlan.priceLabel}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Comparison table (read-only) */}
                    <div
                        className="overflow-x-auto rounded-2xl border border-gray-200 shadow-sm mb-8"
                        data-testid="comparison-table"
                    >
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                                    Feature
                                </th>
                                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                                    Free
                                </th>
                                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                                    Premium
                                </th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                            <tr data-testid="row-total-clients">
                                <td className="px-6 py-4 text-sm">Total Clients</td>
                                <td className="px-6 py-4 text-sm text-center">5</td>
                                <td className="px-6 py-4 text-sm text-center">Unlimited</td>
                            </tr>
                            <tr data-testid="row-interactions">
                                <td className="px-6 py-4 text-sm">Record Interactions</td>
                                <td className="px-6 py-4 text-sm text-center">3 per client</td>
                                <td className="px-6 py-4 text-sm text-center">Unlimited</td>
                            </tr>
                            <tr data-testid="row-follow-ups">
                                <td className="px-6 py-4 text-sm">Set Follow-ups</td>
                                <td className="px-6 py-4 text-sm text-center">3 per client</td>
                                <td className="px-6 py-4 text-sm text-center">Unlimited</td>
                            </tr>
                            <tr data-testid="row-contacts">
                                <td className="px-6 py-4 text-sm">Add client contacts</td>
                                <td className="px-6 py-4 text-sm text-center">3 per client</td>
                                <td className="px-6 py-4 text-sm text-center">Unlimited</td>
                            </tr>
                            <tr data-testid="row-ai">
                                <td className="px-6 py-4 text-sm">AI features</td>
                                <td className="px-6 py-4 text-sm text-center text-red-500">
                                    <X className="mx-auto h-5 w-5"/>
                                </td>
                                <td className="px-6 py-4 text-sm text-center text-green-600">
                                    <Check className="mx-auto h-5 w-5"/>
                                </td>
                            </tr>
                            <tr data-testid="row-files">
                                <td className="px-6 py-4 text-sm">File uploads</td>
                                <td className="px-6 py-4 text-sm text-center text-red-500">
                                    <X className="mx-auto h-5 w-5"/>
                                </td>
                                <td className="px-6 py-4 text-sm text-center text-green-600">
                                    <Check className="mx-auto h-5 w-5"/>
                                </td>
                            </tr>
                            <tr className="bg-gray-50" data-testid="row-price">
                                <td className="px-6 py-4 text-sm font-medium">Price</td>
                                <td className="px-6 py-4 text-sm text-center font-semibold">$0</td>
                                <td className="px-6 py-4 text-sm text-center font-semibold">
                                    $9.99 / mo
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    <form onSubmit={handleContinue} className="flex items-center justify-between">
                        <p className="text-sm text-gray-600">
                            Selected plan:{' '}
                            <span data-testid="selected-plan" className="font-semibold">
                                Premium
                            </span>
                        </p>
                        <button
                            type="submit"
                            disabled={!canSubmit}
                            data-testid="continue-btn"
                            className={`inline-flex items-center justify-center rounded-2xl px-5 py-2.5 text-white shadow-md transition ${
                                canSubmit ? 'bg-crumborange hover:brightness-95'
                                    : 'bg-gray-300 cursor-not-allowed'
                            }`}
                        >
                            Continue to Checkout
                        </button>
                    </form>
                </section>
            </main>
            <Footer />
            <ErrorToast message={errorMessage} clearError={() => setErrorMessage('')} />
        </div>
    );
};

export default ChangeSubscriptionPage;

